<template>
  <div class="search">
    <div class="search-field-selector">
      <div class="search-field-selector__dropdown">
        <h6
          v-click-outside="outsideClicked"
          @click="isActiveDropdown = !isActiveDropdown"
        >
          {{ dropdownLabel }}
          <span
            v-if="!isSingleOptions"
            :class="{ active: isActiveDropdown }"
            class="caret"
          />
        </h6>
        <ul v-if="isActiveDropdown && !isSingleOptions">
          <li
            v-for="(option, index) in options"
            :key="index"
            class="option"
            @click="selectDropdownOption(option)"
          >
            {{ option.label }}
            <svg-loader
              v-if="currentOption?.id === option.id"
              src="/static/images/check-dropdown.svg"
            />
          </li>
        </ul>
      </div>
      <div class="search-field-selector__field">
        <input
          v-if="shouldDisplayTextInput"
          v-model="query"
          maxlength="199"
          :placeholder="dropdownPlaceholder"
          data-vv-validate-on="change|blur"
          :name="type"
          @input="notifyParent"
          @keydown.enter="onEnterPressed"
        />

        <DatePicker
          v-if="type === 'singleDate'"
          :date-selected="singleDate"
          :date-filter-alternative="true"
          :limit-data="limitDateComputed"
          :calendar-direction="'bottom'"
          date-type="single"
          @update:dateSelected="onSingleDateChange"
        />

        <DatePickerRange
          v-else-if="type === 'date' || type === 'dateRange'"
          :date-selected="dateRange"
          :date-filter-alternative="true"
          :limit-data="limitDateComputed"
          :calendar-direction="'bottom'"
          :clear-button="clearButton"
          :default-start-date="defaultStartDate"
          :default-end-date="defaultEndDate"
          @update:dateSelected="onDateRangeChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DatePickerRange from '@/components/common/DatePickerRange';
import DatePicker from '@/components/common/DatePicker';
import Moment from 'moment';
import { clickOutside } from '@/utils/directives';

export default {
  name: 'SearchFieldSelectorV2',
  components: {
    DatePickerRange,
    DatePicker,
  },
  directives: {
    'click-outside': {
      bind: function (el, binding, vnode) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unbind: function (el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      },
    },
    clickOutside,
  },
  props: {
    selectedOption: {
      type: Object,
      default: null,
    },
    /**
     * @property {Array<Object>} options - Array of option objects for the dropdown.
     * Each object in the array should have:
     *  @property {String} id - The unique identifier for the option.
     *  @property {String} label - The display label for the option in the dropdown.
     *  @property {String} type - The type of input this option represents (e.g., 'date', 'singleDate', 'text').
     *  @property {String} [placeholder] - Optional placeholder text for the input field when this option is selected.
     *  @property {Number} [limitDays] - Optional number of days to limit date selection in date pickers.
     */
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    /**
     * @property {Object} defaultOption - The default option to initialize the dropdown with.
     */
    defaultOptionIndex: {
      type: Number,
      default: 0,
    },
    textValue: {
      type: String,
      default: '',
    },
    singleDateValue: {
      type: Date,
      default: null,
    },
    dateRangeValue: {
      type: Object,
      default: () => ({ start: null, end: null }),
    },
    defaultStartDate: {
      type: Date | String,
      default() {
        return new Date();
      },
    },
    defaultEndDate: {
      type: Date | String,
      default() {
        return new Date();
      },
    },
    clearButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      query: this.textValue,
      dateRange: this.dateRangeValue,
      singleDate: this.singleDateValue,
      isActiveDropdown: false,
      currentOption: this.selectedOption || (this.defaultOptionIndex ? this.options[this.defaultOptionIndex] : this.options[0]) || null,
    };
  },
  computed: {
    dropdownLabel() {
      return this.currentOption?.label ?? '';
    },
    dropdownPlaceholder() {
      return this.currentOption?.placeholder ?? '';
    },
    type() {
      return this.currentOption ? this.currentOption?.type : '';
    },
    today() {
      return Moment(new Date()).format('DD/MM/YYYY');
    },
    limitDateComputed() {
      const { limitDate, limitDays } = this.currentOption || {};
      if (limitDate) return limitDate;

      return limitDays ? Moment(new Date()).subtract(limitDays, 'days').toDate() : Moment(new Date()).toDate();
    },
    shouldDisplayTextInput() {
      return this.type !== 'date' && this.type !== 'singleDate';
    },
    isSingleOptions() {
      return this.options.length === 1;
    },
  },
  watch: {
    selectedOption: {
      handler(newValue) {
        if (newValue && newValue !== this.currentOption) {
          this.currentOption = newValue;
        }
      },
      immediate: true,
    },
    textValue(newValue) {
      this.query = newValue;
    },
    singleDateValue(newValue) {
      this.singleDate = newValue;
    },
    dateRangeValue(newValue) {
      this.dateRange = newValue;
    },
    query: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit('update:textValue', newValue);
        }
      },
      deep: true,
      immediate: true,
    },
    singleDate: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit('update:singleDateValue', newValue);
        }
      },
      deep: true,
      immediate: true,
    },
    dateRange: {
      handler(newValue, oldValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          this.$emit('update:dateRangeValue', newValue);
        }
      },
      deep: true,
      immediate: true,
    },
    defaultOptionIndex: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue && newValue !== null && this.options[newValue]) {
          this.currentOption = this.options[newValue];
        }
      },
      immediate: true,
    },
  },
  methods: {
    selectDropdownOption(option) {
      this.isActiveDropdown = false;
      this.currentOption = option;

      if (this.type === 'date' || this.type === 'dateRange') {
        const currentDate = new Date();
        this.dateRange = {
          start: this.defaultStartDate ?? currentDate,
          end: this.defaultEndDate ?? currentDate,
        };
      }

      this.notifyParent();
    },
    notifyParent() {
      const searchData = {
        ...this.currentOption,
        type: this.type,
        query: this.query,
        date: this.dateRange,
        singleDate: this.singleDate,
      };
      this.$emit('updateSearchValue', searchData);
    },
    outsideClicked() {
      this.isActiveDropdown = false;
    },
    onSingleDateChange(newValue) {
      this.singleDate = newValue;
      this.notifyParent();
    },
    onDateRangeChange(newValue) {
      this.dateRange = newValue;
      this.notifyParent();
    },
    onEnterPressed() {
      this.$emit('enterPressed');
    },
  },
};
</script>

<style lang="scss">
.search {
  margin-top: 0.5em;
  width: 100%;

  .search-field-selector {
    display: flex;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 100%;
    flex-wrap: nowrap;
    justify-content: space-between;
    min-height: 40px;
    align-items: center;

    h6 {
      display: flex;
      justify-content: space-between;
      align-items: center;
      min-width: 150px;
      padding: 8px;
      font-weight: 500;
      font-size: 13px;
      color: #999;
      cursor: pointer;

      .caret {
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 6px solid #7f1a54;
        margin-left: 4px;
        border-radius: 6px;
        transform: rotate(180deg);
        transition: all ease-out 0.3s;

        &.active {
          transform: rotate(0deg);
        }
      }
    }

    &__dropdown {
      border-right: 1px solid #ccc;
      position: relative;

      ul {
        width: 142px;
        display: flex;
        flex-wrap: wrap;
        padding: 8px;
        background: #ffffff;
        box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
        border-radius: 4px;
        position: absolute;
        left: 0;
        top: 44px;
        z-index: 1;
      }

      li {
        width: 100%;
        padding: 9px;
        margin: 0;
        font-size: 14px;
        color: #363636;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #ffffff;
        transition: all ease 0.3s;
        cursor: pointer;

        &:hover {
          background-color: #e9e9e9;
          border-radius: 4px;
        }
      }
    }

    &__field {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-grow: 1;
      flex-shrink: 1;
      padding: 0 8px;

      input {
        font-weight: 500;
        font-size: 13px;
        line-height: 16px;
        color: #999999;
        height: 50px;
        border: none;
        flex-grow: 1;
        outline: none;
        background-color: transparent;
      }

      #datePickerSelect {
        margin: 0;
        padding: 0;
        align-items: center;
      }

      #datePickerSingleSelect {
        min-height: inherit;
        margin: 0;
        padding: 0;
        align-items: center;
      }
    }
  }
}
</style>
